import { Card, Colors, Heading, Icon, Text, type IconProps } from '@amelio/ui-library';

import type { ReactNode } from 'react';

/**
 * Props for the FeatureItem component.
 *
 * @property icon - Name of the icon from the UI library to display (e.g., 'business-status-up', 'time-clock', 'security-shield-tick')
 * @property heading - Main heading/title text
 * @property description - Supporting description text
 * @property className - Additional CSS classes for custom styling
 * @property dataTestId - Data test ID for testing
 */
export interface FeatureItemProps {
    /** Icon name from UI library - any available icon can be used */
    icon?: IconProps['name'];
    /** Main heading/title text */
    heading: string;
    /** Supporting description text */
    description: string | ReactNode;
    /** Additional CSS classes for custom styling */
    className?: string;
    /** Data test ID for testing */
    dataTestId?: string;

    weight?: 'bold' | 'light' | 'regular' | 'medium' | 'semibold';
    /** `compact`: 60px icon aligned to the heading, 24px heading, 11px description, no inner padding */
    variant?: 'default' | 'compact';
}

/**
 * FeatureItem component displays an icon with heading and description.
 * Used for highlighting key features or benefits in a visually distinct format.
 *
 * @example
 * <FeatureItem
 *   icon="security-shield-tick"
 *   heading="Development-focused"
 *   description="Responses are visible to managers to support meaningful improvement."
 * />
 */
export const FeatureItem = ({
    icon,
    heading,
    description,
    className = '',
    dataTestId = 'feature-item',
    weight = 'medium',
    variant = 'default',
}: FeatureItemProps) => {
    const isCompact = variant === 'compact';

    return (
        <Card
            padding={'none'}
            orientation={'horizontal'}
            className={`flex w-full items-start gap-4 ${isCompact ? 'p-6' : 'px-5 py-4'} ${className}`}
            dataTestId={dataTestId}
            borderRadius={'2xl'}
            borderColor={Colors.WHITE}
        >
            {/* Icon container with fixed turquoise background */}
            {icon && (
                <div
                    className={`bg-primary-50 flex aspect-square shrink-0 items-center justify-center rounded-full ${isCompact ? 'h-15 w-15' : 'h-16 w-16 self-center'}`}
                    aria-hidden={'true'}
                >
                    <Icon
                        name={icon}
                        size={isCompact ? 36 : 32}
                        color={Colors.PRIMARY}
                        ariaLabel={`Icon for ${heading}`}
                    />
                </div>
            )}

            {/* Text content */}
            <div className={`flex flex-1 flex-col ${isCompact ? 'gap-2' : 'sm:gap-1 md:p-11'}`}>
                <Heading
                    level={3}
                    weight={weight}
                    color={Colors.BASE}
                    className={`wrap-break-word ${isCompact ? 'text-2xl leading-tight' : ''}`}
                >
                    {heading}
                </Heading>

                <Text
                    size={'default'}
                    color={Colors.BASE}
                    className={`wrap-break-word ${isCompact ? 'text-[11px] leading-normal' : 'leading-relaxed'}`}
                >
                    {description}
                </Text>
            </div>
        </Card>
    );
};
